// Tree → GEDCOM 5.5.1 (default, most widely readable) or 7.0.
//
// Our pairwise links have to be folded back into FAM records:
//  - every partnership becomes a FAM;
//  - each child's set of parents is covered by FAMs, preferring existing partnerships,
//    then single-parent or "co-parent" FAMs for the rest (three-parent families end up
//    in two FAMs, each listing the child);
//  - per-parent link types go in Ancestry/FTM-style _FREL/_MREL under CHIL, which most
//    desktop programs understand, plus standard PEDI on FAMC when it's unambiguous.
// Extensions use underscore tags (_GENDER, _PRONOUNS, _GROUP…). GEDCOM 7 output declares them in HEAD.SCHMA.

import { parseGedcomDate } from '../model/dates';
import { graphOf, parentsOf } from '../model/graph';
import { isHiddenName, isLiving } from '../model/people';
import { placeKey } from '../model/places';
import type { Citation, Id, LifeEvent, MediaItem, ParentRel, ParentType, PartnerRel, Person, PersonName, RawRecord, Relationship, Tree } from '../model/types';
import { EVEN_TYPE_LABELS } from './gedcom-import';
import { rec, writeGedcom } from './gedcom-lines';

export interface ExportOptions {
  version: '5.5.1' | '7.0';
  /** Include names marked hidden (e.g. deadnames). Off by default. */
  includeHiddenNames: boolean;
  /** Replace living people's details with "Living". */
  privatizeLiving: boolean;
  /** Include GEDCOM records preserved from an earlier import (sources, religious records, media…). */
  includePreserved: boolean;
}

export const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  version: '5.5.1',
  includeHiddenNames: false,
  privatizeLiving: false,
  includePreserved: true,
};

const EXTENSION_TAGS = [
  '_RID', '_GENDER', '_PRONOUNS', '_LIVING', '_TAG', '_HIDDEN', '_FREL', '_MREL', '_PTYPE', '_STAT', '_TYPE',
  '_LABEL', '_DATE', '_GROUP', '_MEMB', '_COLOR', '_TREE', '_HOME', '_TERMS',
  '_TODO', '_PRIO', '_DUE', '_DONE', '_REGION', '_URL', '_END', '_PLACE', '_CREATED',
];

const STANDARD_EVENT_TAG: Partial<Record<LifeEvent['type'], string>> = {
  birth: 'BIRT', death: 'DEAT', burial: 'BURI', cremation: 'CREM', residence: 'RESI', education: 'EDUC',
  graduation: 'GRAD', occupation: 'OCCU', immigration: 'IMMI', emigration: 'EMIG', naturalization: 'NATU',
  census: 'CENS', retirement: 'RETI', adoption: 'ADOP',
};

const REL_TEXT: Record<ParentType, string> = {
  biological: 'Natural', adoptive: 'Adopted', step: 'Step', foster: 'Foster', guardian: 'Guardian',
  chosen: 'Chosen', surrogate: 'Surrogate', donor: 'Donor', unknown: 'Unknown',
};

const SOCIAL_RELA: Record<string, string> = {
  friend: 'Friend', 'close-friend': 'Close friend', 'best-friend': 'Best friend',
  'family-friend': 'Family friend', mentor: 'Mentor', roommate: 'Roommate', colleague: 'Colleague', neighbor: 'Neighbor',
  other: 'Other',
};

const SIBLING_RELA: Record<string, string> = {
  half: 'Half sibling', step: 'Stepsibling', adoptive: 'Adoptive sibling', foster: 'Foster sibling', chosen: 'Chosen sibling',
};

function dateRecord(date: string | undefined, v: ExportOptions['version']): RawRecord | undefined {
  if (!date) return undefined;
  const p = parseGedcomDate(date);
  if (p?.phrase && !p.start) {
    return v === '7.0' ? rec('DATE', '', [rec('PHRASE', p.phrase)]) : rec('DATE', `(${p.phrase})`);
  }
  return rec('DATE', date);
}

function nameRecord(n: PersonName, v: ExportOptions['version']): RawRecord {
  const value = [n.prefix, n.given, n.surname ? `/${n.surname}/` : n.given ? '//' : '', n.suffix].filter(Boolean).join(' ').trim();
  const children: RawRecord[] = [];
  if (n.prefix) children.push(rec('NPFX', n.prefix));
  if (n.given) children.push(rec('GIVN', n.given));
  if (n.nickname) children.push(rec('NICK', n.nickname));
  if (n.surname) children.push(rec('SURN', n.surname));
  if (n.suffix) children.push(rec('NSFX', n.suffix));
  // A plain name is an untyped NAME; every other kind says what it is.
  if (n.type !== 'name') {
    if (v === '7.0') {
      const std: Record<Exclude<PersonName['type'], 'name'>, string> = {
        birth: 'BIRTH', married: 'MARRIED', alias: 'AKA', chosen: 'OTHER', deadname: 'OTHER', other: 'OTHER',
      };
      const t = rec('TYPE', std[n.type]);
      if (std[n.type] === 'OTHER') t.children.push(rec('PHRASE', n.type === 'chosen' ? 'Chosen name' : n.type === 'deadname' ? 'Deadname' : 'Other'));
      children.push(t);
    } else {
      const map: Record<Exclude<PersonName['type'], 'name'>, string> = { birth: 'birth', married: 'married', alias: 'aka', chosen: 'chosen', deadname: 'deadname', other: 'other' };
      children.push(rec('TYPE', map[n.type]));
    }
  }
  if (n.hidden) children.push(rec('_HIDDEN', 'Y'));
  return rec('NAME', value || '//', children);
}

/** Citation structures: `SOUR @S1@` with PAGE, QUAY, and NOTE. */
type CiteWriter = (cs: Citation[] | undefined) => RawRecord[];

function eventRecord(e: LifeEvent, v: ExportOptions['version'], cite: CiteWriter): RawRecord {
  const std = STANDARD_EVENT_TAG[e.type];
  let r: RawRecord;
  if (std) {
    const valued = e.type === 'occupation' || e.type === 'education';
    r = rec(std, valued ? e.label : undefined);
    if (!valued && e.label) r.children.push(rec('TYPE', e.label));
  } else {
    const label = e.type === 'custom' ? e.label || 'Event' : EVEN_TYPE_LABELS[e.type] ?? 'Event';
    r = rec('EVEN', undefined, [rec('TYPE', label)]);
  }
  const d = dateRecord(e.date, v);
  if (d) r.children.push(d);
  const placeExtra = e.passthrough?.find((x) => x.tag === '__PLAC');
  if (e.place) r.children.push(rec('PLAC', e.place, placeExtra?.children ?? []));
  if (e.note) r.children.push(rec('NOTE', e.note));
  r.children.push(...cite(e.citations));
  for (const x of e.passthrough ?? []) if (x.tag !== '__PLAC') r.children.push(x);
  // A bare event with no details still needs a value in 5.5.1 to assert it happened.
  if (!r.value && !r.children.length && (e.type === 'death' || e.type === 'birth')) r.value = 'Y';
  return r;
}

function sexValue(p: Person, v: ExportOptions['version']): string {
  switch (p.gender.identity) {
    case 'woman':
    case 'trans-woman':
    case 'transfeminine': return 'F';
    case 'man':
    case 'trans-man':
    case 'transmasculine': return 'M';
    case 'unknown': return 'U';
    default: return v === '7.0' ? 'X' : 'U';
  }
}

function today(): string {
  const d = new Date();
  return `${d.getDate()} ${['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][d.getMonth()]} ${d.getFullYear()}`;
}

function collectXrefs(records: RawRecord[], into: Set<string>) {
  for (const r of records) {
    if (r.xref) into.add(r.xref);
    collectXrefs(r.children, into);
  }
}

export function exportGedcom(tree: Tree, options: Partial<ExportOptions> = {}): string {
  const opts = { ...DEFAULT_EXPORT_OPTIONS, ...options };
  const v = opts.version;
  const g = graphOf(tree);
  const people = Object.values(tree.people);

  // ---- xref allocation (keep imported xrefs; never collide with preserved records).
  const used = new Set<string>();
  if (opts.includePreserved) collectXrefs(tree.passthrough ?? [], used);
  const indiXref = new Map<Id, string>();
  for (const p of people) if (p.gedcomId && !used.has(p.gedcomId)) {
    indiXref.set(p.id, p.gedcomId);
    used.add(p.gedcomId);
  }
  const alloc = (prefix: string) => {
    let n = 1;
    while (used.has(`@${prefix}${n}@`)) n++;
    const x = `@${prefix}${n}@`;
    used.add(x);
    return x;
  };
  for (const p of people) if (!indiXref.has(p.id)) indiXref.set(p.id, alloc('I'));
  // Sources and media keep their imported xrefs too, since preserved records may point at them.
  const keepOrAlloc = <T extends { id: Id; gedcomId?: string }>(items: T[], prefix: string) => {
    const map = new Map<Id, string>();
    for (const x of items) if (x.gedcomId && !used.has(x.gedcomId)) {
      map.set(x.id, x.gedcomId);
      used.add(x.gedcomId);
    }
    for (const x of items) if (!map.has(x.id)) map.set(x.id, alloc(prefix));
    return map;
  };
  const sourceXref = keepOrAlloc(Object.values(tree.sources ?? {}), 'S');
  const mediaXref = keepOrAlloc(Object.values(tree.media ?? {}), 'O');
  const todoXref = keepOrAlloc(Object.values(tree.todos ?? {}), 'T');
  const cite: CiteWriter = (cs) =>
    (cs ?? []).flatMap((c) => {
      const x = sourceXref.get(c.sourceId);
      if (!x) return [];
      const r = rec('SOUR', x);
      if (c.page) r.children.push(rec('PAGE', c.page));
      if (c.quality !== undefined) r.children.push(rec('QUAY', String(c.quality)));
      if (c.note) r.children.push(rec('NOTE', c.note));
      return [r];
    });
  /**
   * A link's own details as our extension tags (plus NOTE and SOUR), for links GEDCOM has no
   * place for: parent links, friendships, siblings, and couples who aren't married or engaged.
   * `skip` names what a standard record already carries. Nothing for privatized living people.
   */
  const linkDetails = (r: Relationship, skip: ('start' | 'end' | 'place')[] = []): RawRecord[] => {
    if (opts.privatizeLiving && [r.a, r.b].some((id) => isLiving(tree.people[id]))) return [];
    const out: RawRecord[] = [];
    if (r.label) out.push(rec('_LABEL', r.label));
    if (r.start && !skip.includes('start')) out.push(rec('_DATE', r.start));
    if (r.end && !skip.includes('end')) out.push(rec('_END', r.end));
    if (r.place && !skip.includes('place')) out.push(rec('_PLACE', r.place));
    if (r.notes) out.push(rec('NOTE', r.notes));
    out.push(...cite(r.citations));
    return out;
  };
  const mediaOfPerson = new Map<Id, { item: MediaItem; region?: string }[]>();
  for (const m of Object.values(tree.media ?? {}))
    for (const x of m.people) {
      const region = x.region ? [x.region.x, x.region.y, x.region.w, x.region.h].map((n) => +n.toFixed(5)).join(' ') : undefined;
      mediaOfPerson.set(x.personId, [...(mediaOfPerson.get(x.personId) ?? []), { item: m, region }]);
    }
  const todosOfPerson = new Map<Id, string[]>();
  for (const td of Object.values(tree.todos ?? {})) for (const pid of td.people) todosOfPerson.set(pid, [...(todosOfPerson.get(pid) ?? []), todoXref.get(td.id)!]);

  // ---- FAM construction
  interface Fam {
    xref: string;
    husb?: Id;
    wife?: Id;
    rel?: PartnerRel;
    children: { id: Id; frel?: ParentRel; mrel?: ParentRel }[];
  }
  const fams: Fam[] = [];
  const famByPair = new Map<string, Fam>();
  const famsOfPerson = new Map<Id, Fam[]>();
  const pairKey = (a?: Id, b?: Id) => [a ?? '', b ?? ''].sort().join('|');
  const register = (f: Fam) => {
    fams.push(f);
    for (const id of [f.husb, f.wife]) if (id) famsOfPerson.set(id, [...(famsOfPerson.get(id) ?? []), f]);
  };
  const slotOrder = (a: Id, b: Id): [Id, Id] => {
    // Put a man in HUSB and a woman in WIFE when that's unambiguous; otherwise keep order.
    const ga = tree.people[a]?.gender.identity;
    const gb = tree.people[b]?.gender.identity;
    if (ga === 'woman' && gb === 'man') return [b, a];
    if (gb === 'woman' && ga === 'man') return [a, b];
    if (ga === 'woman' && gb !== 'woman') return [b, a];
    return [a, b];
  };

  const partnerRels = Object.values(tree.relationships).filter((r): r is PartnerRel => r.kind === 'partner' && !!tree.people[r.a] && !!tree.people[r.b]);
  // Current partnerships first so children prefer them.
  partnerRels.sort((x, y) => (x.status === 'current' ? 0 : 1) - (y.status === 'current' ? 0 : 1));
  for (const r of partnerRels) {
    const [h, w] = slotOrder(r.a, r.b);
    const f: Fam = { xref: alloc('F'), husb: h, wife: w, rel: r, children: [] };
    register(f);
    if (!famByPair.has(pairKey(h, w))) famByPair.set(pairKey(h, w), f);
  }

  const childFams = new Map<Id, Fam[]>();
  for (const c of people) {
    const prels = g.parentRels.get(c.id) ?? [];
    if (!prels.length) continue;
    const relOf = new Map(prels.map((r) => [r.a, r]));
    const uncovered = new Set(prels.map((r) => r.a));
    const assigned: Fam[] = [];
    // 1. Existing partnerships whose both members are parents of this child.
    for (const f of fams) {
      if (!f.rel || !f.husb || !f.wife) continue;
      if (uncovered.has(f.husb) && uncovered.has(f.wife)) {
        assigned.push(f);
        uncovered.delete(f.husb);
        uncovered.delete(f.wife);
      }
    }
    // 2. Pair up the rest (co-parents who aren't partners), then single parents.
    const rest = [...uncovered];
    while (rest.length) {
      const a = rest.shift()!;
      const b = rest.shift();
      const key = pairKey(a, b);
      let f = famByPair.get(key);
      if (!f) {
        const [h, w] = b ? slotOrder(a, b) : tree.people[a]?.gender.identity === 'woman' ? [undefined, a] : [a, undefined];
        f = { xref: alloc('F'), husb: h, wife: w, children: [] };
        register(f);
        famByPair.set(key, f);
      }
      assigned.push(f);
    }
    for (const f of assigned) {
      f.children.push({ id: c.id, frel: f.husb ? relOf.get(f.husb) : undefined, mrel: f.wife ? relOf.get(f.wife) : undefined });
      childFams.set(c.id, [...(childFams.get(c.id) ?? []), f]);
    }
  }

  // Explicit full/unknown siblings with no shared recorded parent → parentless FAM per group.
  const sibUnion = new Map<Id, Id>();
  const find = (x: Id): Id => {
    while (sibUnion.get(x) !== x) x = sibUnion.get(x)!;
    return x;
  };
  for (const r of Object.values(tree.relationships)) {
    if (r.kind !== 'sibling' || (r.type !== 'unknown' && r.type !== 'full')) continue;
    if (!tree.people[r.a] || !tree.people[r.b]) continue;
    const pa = new Set(parentsOf(g, r.a));
    if (parentsOf(g, r.b).some((p) => pa.has(p))) continue;
    for (const x of [r.a, r.b]) if (!sibUnion.has(x)) sibUnion.set(x, x);
    sibUnion.set(find(r.a), find(r.b));
  }
  const sibGroups = new Map<Id, Id[]>();
  for (const x of sibUnion.keys()) sibGroups.set(find(x), [...(sibGroups.get(find(x)) ?? []), x]);
  for (const members of sibGroups.values()) {
    const f: Fam = { xref: alloc('F'), children: members.map((id) => ({ id })) };
    register(f);
    for (const id of members) childFams.set(id, [...(childFams.get(id) ?? []), f]);
  }

  // ---- ASSO lines (social ties and non-standard sibling links)
  const asso = new Map<Id, RawRecord[]>();
  const addAsso = (owner: Id, target: Id, text: string, ourType: string, extra: RawRecord[] = []) => {
    const r = rec('ASSO', indiXref.get(target)!);
    if (v === '7.0') {
      const std = ourType === 'social:friend' ? 'FRIEND' : ourType === 'social:neighbor' ? 'NGHBR' : 'OTHER';
      const role = rec('ROLE', std);
      if (std === 'OTHER') role.children.push(rec('PHRASE', text));
      r.children.push(role);
    } else r.children.push(rec('RELA', text));
    r.children.push(rec('_TYPE', ourType), ...extra);
    asso.set(owner, [...(asso.get(owner) ?? []), r]);
  };
  for (const r of Object.values(tree.relationships)) {
    if (!tree.people[r.a] || !tree.people[r.b]) continue;
    if (r.kind === 'social') {
      const extra = linkDetails(r);
      if (r.type === 'mentor') addAsso(r.b, r.a, r.label || 'Mentor', 'social:mentor', extra); // b's mentor is a
      else addAsso(r.a, r.b, r.label || SOCIAL_RELA[r.type], `social:${r.type}`, extra);
    } else if (r.kind === 'sibling' && SIBLING_RELA[r.type]) {
      addAsso(r.a, r.b, SIBLING_RELA[r.type], `sibling:${r.type}`, linkDetails(r));
    }
  }

  // ---- HEAD
  const head = rec('HEAD');
  if (v === '7.0') {
    head.children.push(rec('GEDC', undefined, [rec('VERS', '7.0')]));
    head.children.push(rec('SCHMA', undefined, EXTENSION_TAGS.map((t) => rec('TAG', `${t} urn:relationships:gedcom:${t.slice(1).toLowerCase()}`))));
  } else {
    head.children.push(rec('GEDC', undefined, [rec('VERS', '5.5.1'), rec('FORM', 'LINEAGE-LINKED')]));
    head.children.push(rec('CHAR', 'UTF-8'));
  }
  head.children.push(rec('SOUR', 'RELATIONSHIPS', [rec('NAME', 'Relationships'), rec('VERS', '0.1.0')]));
  head.children.push(rec('DATE', today()));
  if (v === '5.5.1') head.children.push(rec('SUBM', '@SUBM1@'));
  head.children.push(rec('_TREE', tree.name));
  if (tree.homePersonId && indiXref.has(tree.homePersonId)) head.children.push(rec('_HOME', indiXref.get(tree.homePersonId)!));

  const out: RawRecord[] = [head];
  if (v === '5.5.1') {
    used.add('@SUBM1@');
    out.push(rec('SUBM', undefined, [rec('NAME', 'Relationships user')], '@SUBM1@'));
  }

  // ---- INDI
  for (const p of people) {
    const xref = indiXref.get(p.id)!;
    const r = rec('INDI', undefined, [], xref);
    const priv = opts.privatizeLiving && isLiving(p);
    if (priv) {
      r.children.push(rec('NAME', 'Living //', [rec('GIVN', 'Living')]));
    } else {
      const names = opts.includeHiddenNames ? p.names : p.names.filter((n) => !isHiddenName(n));
      for (const n of names.length ? names : [{ given: '', surname: '', type: 'name' as const }]) r.children.push(nameRecord(n, v));
    }
    r.children.push(rec('SEX', sexValue(p, v)));
    // Its id here, so adding the file back to this tree recognizes everyone (gedcom-import reads it).
    r.children.push(rec('_RID', p.id));
    const gv = p.gender.identity === 'custom' ? p.gender.label || 'custom' : p.gender.identity;
    if (p.gender.identity !== 'unknown') r.children.push(rec('_GENDER', gv));
    if (p.pronouns) r.children.push(rec('_PRONOUNS', p.pronouns));
    if (p.terms && p.terms !== 'auto') r.children.push(rec('_TERMS', p.terms));
    if (!priv) {
      for (const e of p.events) r.children.push(eventRecord(e, v, cite));
      if (p.living !== null) r.children.push(rec('_LIVING', p.living ? 'Y' : 'N'));
      for (const t of p.tags) r.children.push(rec('_TAG', t));
      if (p.notes) r.children.push(rec('NOTE', p.notes));
      r.children.push(...cite(p.citations));
      for (const { item, region } of mediaOfPerson.get(p.id) ?? []) r.children.push(rec('OBJE', mediaXref.get(item.id)!, region ? [rec('_REGION', region)] : []));
      for (const x of todosOfPerson.get(p.id) ?? []) r.children.push(rec('_TODO', x));
    } else r.children.push(rec('_LIVING', 'Y'));
    for (const f of childFams.get(p.id) ?? []) {
      const famc = rec('FAMC', f.xref);
      const entry = f.children.find((c) => c.id === p.id);
      const types = [entry?.frel?.type, entry?.mrel?.type].filter((t): t is ParentType => !!t);
      const uniform = types.length && types.every((t) => t === types[0]) ? types[0] : undefined;
      if (uniform === 'adoptive') famc.children.push(rec('PEDI', v === '7.0' ? 'ADOPTED' : 'adopted'));
      else if (uniform === 'foster') famc.children.push(rec('PEDI', v === '7.0' ? 'FOSTER' : 'foster'));
      r.children.push(famc);
    }
    for (const f of famsOfPerson.get(p.id) ?? []) r.children.push(rec('FAMS', f.xref));
    for (const a of asso.get(p.id) ?? []) r.children.push(a);
    if (opts.includePreserved && !priv) for (const x of p.passthrough ?? []) r.children.push(x);
    out.push(r);
  }

  // ---- FAM
  for (const f of fams) {
    const r = rec('FAM', undefined, [], f.xref);
    if (f.husb) r.children.push(rec('HUSB', indiXref.get(f.husb)!));
    if (f.wife) r.children.push(rec('WIFE', indiXref.get(f.wife)!));
    const rel = f.rel;
    if (rel) {
      const priv = opts.privatizeLiving && [rel.a, rel.b].some((id) => isLiving(tree.people[id]));
      const marriedLike = rel.type === 'married' || rel.type === 'civil-union' || rel.type === 'domestic';
      const extra = rel.passthrough ?? [];
      if (marriedLike || rel.status === 'divorced') {
        const m = rec('MARR');
        if (rel.type === 'civil-union') m.children.push(rec('TYPE', 'Civil union'));
        if (rel.type === 'domestic') m.children.push(rec('TYPE', 'Domestic partnership'));
        if (!priv) {
          const d = dateRecord(rel.start, v);
          if (d) m.children.push(d);
          if (rel.place) m.children.push(rec('PLAC', rel.place));
          m.children.push(...(extra.find((x) => x.tag === '__MARR')?.children ?? []));
        }
        if (!m.children.length) m.value = 'Y';
        r.children.push(m);
      } else if (rel.type === 'engaged') {
        const e = rec('ENGA');
        const d = !priv && dateRecord(rel.start, v);
        if (d) e.children.push(d);
        if (!e.children.length) e.value = 'Y';
        r.children.push(e);
      }
      if (rel.status === 'divorced') {
        const d = rec('DIV');
        const dd = !priv && dateRecord(rel.end, v);
        if (dd) d.children.push(dd);
        if (!priv) d.children.push(...(extra.find((x) => x.tag === '__DIV')?.children ?? []));
        if (!d.children.length) d.value = 'Y';
        r.children.push(d);
      }
      r.children.push(rec('_PTYPE', rel.type));
      if (rel.status !== 'current') r.children.push(rec('_STAT', rel.status));
      // What MARR, ENGA, and DIV above don't carry: a label, an end that isn't a divorce, and the
      // start and place of couples who aren't married or engaged.
      const inMarr = marriedLike || rel.status === 'divorced';
      r.children.push(...linkDetails(rel, [...(inMarr || rel.type === 'engaged' ? (['start'] as const) : []), ...(rel.status === 'divorced' ? (['end'] as const) : []), ...(inMarr ? (['place'] as const) : [])]));
      if (opts.includePreserved && !priv) for (const x of extra) if (!x.tag.startsWith('__')) r.children.push(x);
    }
    for (const c of f.children) {
      const cr = rec('CHIL', indiXref.get(c.id)!);
      if (c.frel) cr.children.push(rec('_FREL', REL_TEXT[c.frel.type], linkDetails(c.frel)));
      if (c.mrel) cr.children.push(rec('_MREL', REL_TEXT[c.mrel.type], linkDetails(c.mrel)));
      r.children.push(cr);
    }
    out.push(r);
  }

  // ---- Groups (our extension)
  let gn = 1;
  for (const grp of Object.values(tree.groups)) {
    while (used.has(`@G${gn}@`)) gn++;
    const xref = `@G${gn}@`;
    used.add(xref);
    const r = rec('_GROUP', undefined, [rec('NAME', grp.name), rec('TYPE', grp.kind), rec('_COLOR', grp.color)], xref);
    if (grp.description) r.children.push(rec('NOTE', grp.description));
    for (const m of grp.members) {
      const x = indiXref.get(m.personId);
      if (!x) continue;
      r.children.push(rec('_MEMB', x, m.role ? [rec('ROLE', m.role)] : []));
    }
    out.push(r);
  }

  // ---- Sources, their repositories, media, and to-dos
  const preservedRepos = new Set(opts.includePreserved ? (tree.passthrough ?? []).filter((r) => r.tag === 'REPO' && r.xref).map((r) => r.xref!) : []);
  const repoXref = new Map<string, string>();
  const repoRecords: RawRecord[] = [];
  for (const src of Object.values(tree.sources ?? {})) {
    const r = rec('SOUR', undefined, [rec('TITL', src.title || 'Untitled source')], sourceXref.get(src.id));
    if (src.author) r.children.push(rec('AUTH', src.author));
    if (src.publication) r.children.push(rec('PUBL', src.publication));
    if (src.date || src.place) {
      const ev = rec('EVEN');
      const d = dateRecord(src.date, v);
      if (d) ev.children.push(d);
      if (src.place) ev.children.push(rec('PLAC', src.place));
      r.children.push(rec('DATA', undefined, [ev]));
    }
    if (src.repository || src.callNumber || src.repositoryXref) {
      let x = src.repositoryXref && preservedRepos.has(src.repositoryXref) ? src.repositoryXref : undefined;
      if (!x && src.repository) {
        x = repoXref.get(src.repository);
        if (!x) {
          x = alloc('R');
          repoXref.set(src.repository, x);
          repoRecords.push(rec('REPO', undefined, [rec('NAME', src.repository)], x));
        }
      }
      if (x) r.children.push(rec('REPO', x, src.callNumber ? [rec('CALN', src.callNumber)] : []));
    }
    if (src.text) r.children.push(rec('TEXT', src.text));
    if (src.kind) r.children.push(rec('_TYPE', src.kind));
    if (src.url) r.children.push(rec('_URL', src.url));
    if (src.notes) r.children.push(rec('NOTE', src.notes));
    if (opts.includePreserved) r.children.push(...(src.passthrough ?? []));
    out.push(r);
  }
  out.push(...repoRecords);
  const EXT: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/tiff': 'tif', 'image/heic': 'heic', 'application/pdf': 'pdf', 'audio/mpeg': 'mp3', 'audio/wav': 'wav', 'video/mp4': 'mp4', 'video/quicktime': 'mov' };
  for (const m of Object.values(tree.media ?? {})) {
    const type = m.file?.type ?? '';
    const ext = EXT[type] ?? m.file?.name?.split('.').pop()?.toLowerCase() ?? m.url?.split(/[?#]/)[0].split('.').pop()?.toLowerCase() ?? '';
    // The file itself can't travel in a .ged, so FILE names it; a Relationships backup carries the bytes.
    const path = m.url ?? m.file?.name ?? `${m.title || 'media'}${ext ? '.' + ext : ''}`;
    const form = v === '7.0' ? type || (m.kind === 'link' ? 'text/html' : 'application/octet-stream') : ext && ext.length <= 5 ? ext : m.kind === 'link' ? 'url' : 'unknown';
    const file = rec('FILE', path, [rec('FORM', form)]);
    if (m.title) file.children.push(rec('TITL', m.title));
    const r = rec('OBJE', undefined, [file, rec('_RID', m.id)], mediaXref.get(m.id));
    if (m.date) r.children.push(rec('_DATE', m.date));
    if (m.description) r.children.push(rec('NOTE', m.description));
    for (const sid of m.sourceIds ?? []) if (sourceXref.has(sid)) r.children.push(rec('SOUR', sourceXref.get(sid)!));
    out.push(r);
  }
  for (const td of Object.values(tree.todos ?? {})) {
    const r = rec('_TODO', undefined, [rec('TITL', td.title), rec('_STAT', td.status), rec('_PRIO', td.priority), rec('_RID', td.id)], todoXref.get(td.id));
    if (td.kind) r.children.push(rec('TYPE', td.kind));
    if (td.due) r.children.push(rec('_DUE', td.due));
    if (td.doneAt) r.children.push(rec('_DONE', td.doneAt));
    if (td.createdAt) r.children.push(rec('_CREATED', td.createdAt));
    if (td.notes) r.children.push(rec('NOTE', td.notes));
    out.push(r);
  }

  if (opts.includePreserved) {
    for (const r of tree.passthrough ?? []) {
      // 5.5.1 submitter is regenerated; skip preserved SUBM records with the same xref.
      if (r.tag === 'SUBM' && r.xref === '@SUBM1@') continue;
      out.push(r);
    }
  }
  out.push(rec('TRLR'));
  return writeGedcom(withMapCoordinates(out, tree), { version: v });
}

/**
 * Adds where each place is (`PLAC` › `MAP` › `LATI`/`LONG`) under every place the tree has a
 * location for. Copies what it changes: records can be the tree's own preserved ones.
 */
function withMapCoordinates(records: RawRecord[], tree: Tree): RawRecord[] {
  const locations = tree.locations;
  if (!locations) return records;
  const deg = (n: number, pos: string, neg: string) => `${n < 0 ? neg : pos}${String(Math.abs(Math.round(n * 1e6) / 1e6))}`;
  const visit = (r: RawRecord): RawRecord => {
    let children = r.children.map(visit);
    const loc = r.tag === 'PLAC' && r.value && !children.some((c) => c.tag === 'MAP') ? locations[placeKey(r.value)] : undefined;
    if (loc) children = [...children, rec('MAP', undefined, [rec('LATI', deg(loc.lat, 'N', 'S')), rec('LONG', deg(loc.lon, 'E', 'W'))])];
    return children.some((c, i) => c !== r.children[i]) || children.length !== r.children.length ? { ...r, children } : r;
  };
  return records.map(visit);
}
